/**
 * `/$tenantSlug/settings/terms` — payment-terms settings sub-route.
 *
 * Per docs/plans/complete_destination_plan.md § 5 — Settings: terms.
 *
 * Terms is a Settings concept, NOT a top-level module. This route renders
 * a simple list of the tenant's payment-terms profiles (Net 30, 2/10 Net 30,
 * Due 15th, ...) plus an edit drawer. Auto-grows from QB sync; cockpit
 * supports manual create / edit / archive too.
 *
 * Backed by /api/settings/terms — router_terms.py (mig 0184 / 0185).
 */

import { RequirePermission, useAuthStore } from "@ken/auth";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  Skeleton,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactElement } from "react";

export const Route = createFileRoute("/$tenantSlug/settings/terms")({
  component: TermsSettingsRoute,
});

/**
 * Hard permission gate (defense-in-depth). `payments.terms.view` is a business-data
 * module view (NOT a shell-admin surface), so an advisor — who holds every
 * business-data view per ADR-0090 — is correctly ALLOWED here. The gate only
 * denies users who lack `payments.terms.view`. The parent /settings index hides the
 * card; this blocks direct navigation. Mirrors the approvals route.
 */
function TermsSettingsRoute(): ReactElement {
  return (
    <RequirePermission slug="payments.terms.view">
      <TermsSettingsContent />
    </RequirePermission>
  );
}

interface TermListItem {
  id: string;
  name: string;
  term_type: string | null;
  anchor: string | null;
  due_days: number | null;
  discount_days: number | null;
  due_day_of_month: number | null;
  due_next_month_days: number | null;
  discount_day_of_month: number | null;
  discount_rate: number | null;
  is_active: boolean;
  updated_at: string | null;
  archived_at: string | null;
}

interface TermListResponse {
  items: TermListItem[];
  total: number;
  limit: number;
  offset: number;
  next_cursor: string | null;
}

function formatMathSummary(t: TermListItem): string {
  // anchor='invoice_date' uses due_days; anchor='calendar_day' uses
  // due_day_of_month. Render whichever shape is populated.
  if (t.anchor === "invoice_date" || t.due_days !== null) {
    const parts: string[] = [];
    if (t.discount_rate !== null && t.discount_days !== null) {
      parts.push(`${t.discount_rate}% / ${t.discount_days}d`);
    }
    if (t.due_days !== null) {
      parts.push(`Net ${t.due_days}`);
    }
    return parts.join(" · ") || "—";
  }
  if (t.anchor === "calendar_day" || t.due_day_of_month !== null) {
    const parts: string[] = [];
    if (t.discount_rate !== null && t.discount_day_of_month !== null) {
      parts.push(`${t.discount_rate}% by day ${t.discount_day_of_month}`);
    }
    if (t.due_day_of_month !== null) {
      parts.push(`Due day ${t.due_day_of_month}`);
    }
    if (t.due_next_month_days !== null) {
      parts.push(`+${t.due_next_month_days}d rolls to next month`);
    }
    return parts.join(" · ") || "—";
  }
  return "—";
}

function TermsSettingsContent(): ReactElement {
  const session = useAuthStore((s) => s.session);
  // canView is guaranteed true here (wrapped in RequirePermission "payments.terms.view").
  const canView = session?.permissions.has("payments.terms.view") ?? false;
  const canEdit = session?.permissions.has("payments.terms.edit") ?? false;

  const [items, setItems] = useState<TermListItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch("/api/settings/terms?filter_status=all", {
          credentials: "include",
        });
        if (!res.ok) {
          throw new Error(`list terms failed: ${res.status}`);
        }
        const body = (await res.json()) as TermListResponse;
        if (!cancelled) {
          setItems(body.items);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  const rows = useMemo(() => items ?? [], [items]);

  return (
    <div className="flex flex-col gap-4" data-testid="settings-terms-page">
      <header>
        <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
          Settings
        </div>
        <h1 className="text-[28px] font-bold tracking-tight">Payment terms</h1>
        <p className="text-sm text-[var(--color-text-soft)] mt-1">
          Reusable payment-term profiles (Net 30, 2/10 Net 30, Due 15th, ...)
          used on invoices, bills, purchase orders, and estimates. Auto-grows
          from QuickBooks sync; you can also create or archive terms manually.
        </p>
      </header>

      {error && (
        <Card surface="glass-strong">
          <CardContent>
            <p className="text-sm text-red-400">Error: {error}</p>
          </CardContent>
        </Card>
      )}

      {items === null && !error ? (
        <Skeleton className="h-32 w-full" />
      ) : (
        <Card surface="glass-strong">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Terms ({rows.length})</CardTitle>
              {canEdit && (
                <Button variant="primary" size="sm" disabled>
                  New term
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-sm text-[var(--color-text-soft)]">
                No payment terms yet. Once QuickBooks syncs the company file,
                the seeded terms (Net 30, Due on receipt, ...) appear here.
              </p>
            ) : (
              <table className="w-full text-sm" data-testid="settings-terms-table">
                <thead>
                  <tr className="text-left text-[var(--color-text-faint)]">
                    <th className="py-1 pr-3">Name</th>
                    <th className="py-1 pr-3">Type</th>
                    <th className="py-1 pr-3">Math</th>
                    <th className="py-1 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((t) => (
                    <tr
                      key={t.id}
                      data-testid={`settings-terms-row-${t.id}`}
                      className="border-t border-[var(--color-glass-border)]"
                    >
                      <td className="py-2 pr-3 font-medium">{t.name}</td>
                      <td className="py-2 pr-3">
                        <Chip>{t.term_type ?? "other"}</Chip>
                      </td>
                      <td className="py-2 pr-3 text-[var(--color-text-soft)]">
                        {formatMathSummary(t)}
                      </td>
                      <td className="py-2 pr-3">
                        {t.archived_at ? (
                          <Chip variant="muted">Archived</Chip>
                        ) : t.is_active ? (
                          <Chip variant="success">Active</Chip>
                        ) : (
                          <Chip variant="muted">Inactive</Chip>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
